import { useState, useEffect } from 'react';
import { 
  Avatar,
  Box,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Link, useSidebarOpenState } from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  userProfile: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    cursor: 'pointer',
    textDecoration: 'none',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
    transition: 'background-color 0.2s',
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: (theme.palette as any).navigation?.navItem?.hoverBackground || 'rgba(255, 255, 255, 0.1)',
    },
  },
  userProfileClosed: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
    transition: 'background-color 0.2s',
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: (theme.palette as any).navigation?.navItem?.hoverBackground || 'rgba(255, 255, 255, 0.1)',
    },
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
  username: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
  },
}));

interface UserInfo {
  username?: string;
  picture?: string;
}

export const UserProfile = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const { isOpen } = useSidebarOpenState();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        
        // Get Backstage identity
        const identity = await identityApi.getBackstageIdentity();
        const profile = await identityApi.getProfileInfo();
        
        const info: UserInfo = {
          username: identity.userEntityRef?.split('/')[1] || 'User',
          picture: profile.picture,
        };

        setUserInfo(info);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setUserInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUserInfo, 30000);
    return () => clearInterval(interval);
  }, [identityApi]);

  if (loading) {
    return (
      <Box className={isOpen ? classes.userProfile : classes.userProfileClosed}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!userInfo) {
    return (
      <Link to="/settings" className={isOpen ? classes.userProfile : classes.userProfileClosed}>
        <Avatar className={classes.avatar}>?</Avatar>
        {isOpen && (
          <Typography variant="body2" className={classes.username}>
            Not signed in
          </Typography>
        )}
      </Link>
    );
  }

  return (
    <Link to="/settings" className={isOpen ? classes.userProfile : classes.userProfileClosed}>
      <Avatar 
        src={userInfo.picture} 
        alt={userInfo.username}
        className={classes.avatar}
      >
        {userInfo.username?.[0]?.toUpperCase()}
      </Avatar>
      
      {isOpen && (
        <Typography variant="body2" className={classes.username}>
          {userInfo.username}
        </Typography>
      )}
    </Link>
  );
};